// src/pages/NewHomeTwo.jsx
// Piano With Pan — Landing Page V21.1
//
// Changes from V21:
// ✅ Fullscreen on mobile now locks to landscape orientation (Screen Orientation API)
// ✅ Exiting fullscreen unlocks orientation so the phone returns to its normal state
// ✅ iOS Safari handled via webkitEnterFullscreen (auto-landscape, no lock needed)
// ✅ All other V21 features unchanged

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import AuthModal from "../components/AuthModal";
import VirtualPiano, {
  normalizeNoteName as vpNormalizeNoteName,
} from "../components/VirtualPiano";
import { pixelLead } from "../analytics/metaPixel";
import "./NewHomeTwo.css";

const NAV_LOGO_SRC = "/icons/pwp-192.png";
const DORON_IMG = "/images/Doron.png";
const SLEEPING_DORON_IMG = "/images/sleeping-doron.png";

const HERO_VIDEO_SRC = "/videos/piano-with-pan-landing.mp4";
const HERO_VIDEO_POSTER = "/videos/landing-video-poster.jpg";

/* ══ GA ══ */
const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, parameters);
  }
};

const trackPageView = (page) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("config", "G-TZC5XT1878", { page_path: page });
  }
};

/* ── Melody ── */
const MELODY = [
  { note: "C" },
  { note: "C" },
  { note: "G" },
  { note: "G" },
  { note: "A" },
  { note: "A" },
  { note: "G" },
];

/* ── Content ── */
const OUTCOMES = [
  {
    emoji: "🎹",
    title: "Master Piano Notes",
    desc: "Easy C-D-E alphabet notes for complete beginners.",
    bg: "#6d28d9",
    color: "#fff",
    subColor: "rgba(255,255,255,0.75)",
  },
  {
    emoji: "🎵",
    title: "10 Super Fun Songs",
    desc: "Twinkle, Happy Birthday, National Anthem + 7 more.",
    bg: "#fbbf24",
    color: "#0f172a",
    subColor: "#44403c",
  },
  {
    emoji: "😊",
    title: "Habits that Stick",
    desc: "Build a practice habit — with a smile, not a struggle.",
    bg: "#0f172a",
    color: "#fff",
    subColor: "rgba(255,255,255,0.6)",
  },
];

const FAQS = [
  {
    q: "Do we need a piano?",
    a: "No. The built-in virtual piano works on any phone, tablet or laptop. Add a real keyboard later — turn on the mic and Pan gives real-time feedback.",
  },
  {
    q: "How much time per day?",
    a: "10–15 minutes. Levels 1–3 are completely free, no time limit. Levels 4–10 unlock with Premium (₹299/month).",
  },
  {
    q: "What age is this for?",
    a: "Ages 5 and up. Simple enough for young kids to navigate on their own.",
  },
];

/* ─────────────────────────────────────────────
   Platform detection
───────────────────────────────────────────── */
function detectPlatform() {
  if (typeof navigator === "undefined") {
    return { isIOS: false, isAndroid: false, isInApp: false, isStandalone: false };
  }

  const ua = navigator.userAgent || navigator.vendor || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  const isInApp = /FBAN|FBAV|Instagram|Messenger|Line\//i.test(ua);
  const isStandalone =
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true;

  return { isIOS, isAndroid, isInApp, isStandalone };
}

/* ─────────────────────────────────────────────
   Responsive piano sizing
───────────────────────────────────────────── */
function useViewportWidth() {
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === "undefined") return 1024;
    return window.innerWidth;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateWidth = () => setViewportWidth(window.innerWidth);
    updateWidth();

    window.addEventListener("resize", updateWidth, { passive: true });
    window.addEventListener("orientationchange", updateWidth, { passive: true });

    return () => {
      window.removeEventListener("resize", updateWidth);
      window.removeEventListener("orientationchange", updateWidth);
    };
  }, []);

  return viewportWidth;
}

function getResponsivePianoSize(viewportWidth) {
  if (viewportWidth <= 360) {
    return { keyWidth: 30, whiteKeyHeight: 96, blackKeyWidth: 19, blackKeyHeight: 60 };
  }
  if (viewportWidth <= 430) {
    return { keyWidth: 34, whiteKeyHeight: 102, blackKeyWidth: 21, blackKeyHeight: 64 };
  }
  if (viewportWidth <= 520) {
    return { keyWidth: 37, whiteKeyHeight: 108, blackKeyWidth: 23, blackKeyHeight: 68 };
  }
  return { keyWidth: 40, whiteKeyHeight: 110, blackKeyWidth: 24, blackKeyHeight: 70 };
}

/* ─────────────────────────────────────────────
   NAVBAR
───────────────────────────────────────────── */
function NavBar({ nav, onDownloadClick, showDownload }) {
  const { session, loading, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const hasSession = Boolean(session);

  const onLogin = () => {
    trackEvent("nav_login_click", { location: "navbar" });
    const back = window.location.pathname + (window.location.search || "");
    nav(`/login?back=${encodeURIComponent(back)}`);
  };

  const onLogout = async () => {
    setBusy(true);
    trackEvent("nav_logout_click");
    try {
      await signOut();
    } catch (e) {
      console.warn(e);
    } finally {
      setBusy(false);
      nav("/home", { replace: true });
    }
  };

  return (
    <header className="pwp-nav">
      <button
        type="button"
        onClick={() => nav("/home")}
        className="pwp-nav-logo"
        aria-label="Piano With Pan home"
      >
        <img
          src={NAV_LOGO_SRC}
          alt=""
          className="pwp-nav-logo-img"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <span className="pwp-nav-logo-text">Piano With Pan</span>
      </button>

      <div className="pwp-nav-actions">
        {showDownload && (
          <button
            type="button"
            onClick={onDownloadClick}
            className="pwp-nav-btn pwp-nav-btn-download"
          >
            <span className="pwp-nav-btn-download-icon">⬇</span>
            <span className="pwp-nav-btn-download-label">Get App</span>
          </button>
        )}

        {!loading && hasSession ? (
          <button
            onClick={onLogout}
            disabled={busy}
            className="pwp-nav-btn pwp-nav-btn-logout"
          >
            {busy ? "..." : "Log out"}
          </button>
        ) : (
          <button onClick={onLogin} className="pwp-nav-btn">
            Log in
          </button>
        )}
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────
   DOWNLOAD APP MODAL
───────────────────────────────────────────── */
function DownloadAppModal({ onClose, deferredPrompt, onNativeInstallUsed }) {
  const [platform] = useState(() => detectPlatform());

  useEffect(() => {
    trackEvent("download_modal_shown", {
      is_ios: platform.isIOS,
      is_android: platform.isAndroid,
      is_in_app: platform.isInApp,
      has_native_prompt: Boolean(deferredPrompt),
    });

    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNativeInstall = async () => {
    if (!deferredPrompt) return;
    trackEvent("pwa_install_prompt_shown");
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      trackEvent("pwa_install_prompt_result", { outcome: choice.outcome });
      onNativeInstallUsed();
      if (choice.outcome === "accepted") onClose();
    } catch {
      onNativeInstallUsed();
    }
  };

  const canUseNativePrompt = Boolean(deferredPrompt) && !platform.isInApp;

  return (
    <div className="pwp-modal-overlay" onClick={onClose}>
      <div
        className="pwp-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Download the Piano With Pan app"
      >
        <button type="button" className="pwp-modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="pwp-modal-intro">
          <div className="pwp-modal-eyebrow">Get the app</div>
          <div className="pwp-modal-heading">Add Piano With Pan to your home screen</div>
        </div>

        {platform.isInApp && (
          <div className="pwp-download-warning">
            ⚠️ You&apos;re viewing this inside Instagram or Facebook. Tap{" "}
            <strong>•••</strong> or <strong>⋮</strong> at the top right, then{" "}
            <strong>&ldquo;Open in Browser&rdquo;</strong> first — then follow the steps below.
          </div>
        )}

        {canUseNativePrompt && (
          <button type="button" className="pwp-cta pwp-download-native-btn" onClick={handleNativeInstall}>
            ⬇ Install Now
          </button>
        )}

        <ol className="pwp-download-steps">
          {platform.isIOS ? (
            <>
              <li>Tap the <strong>Share</strong> icon <span className="pwp-download-icon">⬆️</span> at the bottom of Safari.</li>
              <li>Scroll down and tap <strong>&ldquo;Add to Home Screen.&rdquo;</strong></li>
              <li>Tap <strong>&ldquo;Add&rdquo;</strong> in the top right corner.</li>
            </>
          ) : platform.isAndroid ? (
            <>
              <li>Tap the <strong>⋮</strong> menu in the top right of your browser.</li>
              <li>Tap <strong>&ldquo;Add to Home screen&rdquo;</strong> or <strong>&ldquo;Install app.&rdquo;</strong></li>
              <li>Tap <strong>&ldquo;Install&rdquo;</strong> to confirm.</li>
            </>
          ) : (
            <>
              <li>Look for the install icon <span className="pwp-download-icon">⊕</span> in your browser&apos;s address bar.</li>
              <li>Click it, then click <strong>&ldquo;Install.&rdquo;</strong></li>
              <li>Piano With Pan will open like a regular app — no browser tabs.</li>
            </>
          )}
        </ol>

        <p className="pwp-download-note">Works on phones, tablets, and computers. No app store needed.</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   LANDING HERO VIDEO
   Autoplay-muted → "Tap for sound" pill → persistent
   mute/fullscreen controls (top-right, hover-reveal on
   desktop, always-on on touch) → slim progress bar.
   Fullscreen locks to landscape on Android Chrome;
   iOS Safari auto-landscapes via webkitEnterFullscreen.
   Orientation is unlocked again on fullscreen exit.
───────────────────────────────────────────── */
function LandingHeroVideo() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const progressTrackRef = useRef(null);
  const hasAttemptedRef = useRef(false);
  const userPausedRef = useRef(false);
  const blockedRef = useRef(false);

  const [hasEverPlayed, setHasEverPlayed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showUnmutePill, setShowUnmutePill] = useState(false);
  const [progressPct, setProgressPct] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    try { video.setAttribute("webkit-playsinline", "true"); } catch { /* no-op */ }

    const onPlay = () => { setIsPlaying(true); setHasEverPlayed(true); setIsBlocked(false); };
    const onPause = () => setIsPlaying(false);
    const onVolumeChange = () => setIsMuted(video.muted);
    const onTimeUpdate = () => {
      if (video.duration) setProgressPct((video.currentTime / video.duration) * 100);
    };
    const onEnded = () => trackEvent("hero_video_completed");

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("volumechange", onVolumeChange);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onEnded);

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const attemptAutoplay = () => {
      if (hasAttemptedRef.current || userPausedRef.current) return;
      hasAttemptedRef.current = true;

      if (prefersReducedMotion) {
        blockedRef.current = true;
        setIsBlocked(true);
        trackEvent("hero_video_autoplay_skipped", { reason: "reduced_motion" });
        return;
      }

      video.muted = true;
      video.defaultMuted = true;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            setShowUnmutePill(true);
            trackEvent("hero_video_autoplay_started", { muted: true });
          })
          .catch(() => {
            blockedRef.current = true;
            setIsBlocked(true);
            trackEvent("hero_video_autoplay_blocked");
          });
      }
    };

    const tryResume = () => {
      if (userPausedRef.current || blockedRef.current) return;
      if (video.paused && !video.ended) video.play().catch(() => {});
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!hasAttemptedRef.current) attemptAutoplay();
            else tryResume();
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("volumechange", onVolumeChange);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
    };
  }, []);

  const resetIfEnded = (video) => {
    if (video.ended) {
      try { video.currentTime = 0; } catch { /* no-op */ }
    }
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setShowUnmutePill(false);
    trackEvent(video.muted ? "hero_video_mute_click" : "hero_video_unmute_click");
  };

  const handleManualPlayClick = () => {
    const video = videoRef.current;
    if (!video) return;

    userPausedRef.current = false;
    blockedRef.current = false;
    resetIfEnded(video);
    video.muted = false;

    video.play()
      .then(() => { trackEvent("hero_video_manual_play_click"); })
      .catch(() => {
        video.muted = true;
        video.play()
          .then(() => setShowUnmutePill(true))
          .catch(() => { trackEvent("hero_video_manual_play_failed"); });
      });
  };

  const handleVideoTap = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      userPausedRef.current = false;
      blockedRef.current = false;
      resetIfEnded(video);
      video.play().catch(() => {});
    } else {
      video.pause();
      userPausedRef.current = true;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      handleVideoTap();
    }
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    const track = progressTrackRef.current;
    if (!video || !track || !video.duration) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    video.currentTime = ratio * video.duration;
    trackEvent("hero_video_seek", { to_percent: Math.round(ratio * 100) });
  };

  const handleProgressKeyDown = (e) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      video.currentTime = Math.min(video.currentTime + 5, video.duration);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      video.currentTime = Math.max(video.currentTime - 5, 0);
    }
  };

  /* ── Fullscreen + landscape lock ──────────────────────────
     Enter: lock orientation to landscape (Android Chrome).
            iOS Safari auto-landscapes via webkitEnterFullscreen.
     Exit:  unlock orientation so the phone rotates freely again.
  ────────────────────────────────────────────────────────── */
  const handleFullscreenClick = () => {
    const frame = frameRef.current;
    const video = videoRef.current;
    const fsElement = document.fullscreenElement || document.webkitFullscreenElement;

    // ── EXIT fullscreen ──
    if (fsElement) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();

      // Unlock orientation so the device can rotate freely again
      try {
        if (window.screen.orientation && window.screen.orientation.unlock) {
          window.screen.orientation.unlock();
        }
      } catch { /* no-op */ }

      trackEvent("hero_video_fullscreen_exit");
      return;
    }

    // ── ENTER fullscreen ──
    // Build a promise chain so we can lock orientation after the
    // browser confirms fullscreen is active.
    let enterPromise;

    if (frame && frame.requestFullscreen) {
      enterPromise = frame.requestFullscreen();
    } else if (frame && frame.webkitRequestFullscreen) {
      // webkitRequestFullscreen may not return a promise on older Safari
      enterPromise = Promise.resolve(frame.webkitRequestFullscreen());
    } else if (video && video.webkitEnterFullscreen) {
      // iOS Safari: native video fullscreen — auto-landscape, no lock needed
      video.webkitEnterFullscreen();
      trackEvent("hero_video_fullscreen_enter");
      return;
    } else {
      // Nothing supported — bail silently
      return;
    }

    Promise.resolve(enterPromise)
      .then(() => {
        // Lock to landscape on Android Chrome / any browser that supports
        // the Screen Orientation API. Browsers that don't support it will
        // throw and we catch silently.
        if (window.screen.orientation && window.screen.orientation.lock) {
          window.screen.orientation.lock("landscape").catch(() => {});
        }
      })
      .catch(() => {});

    trackEvent("hero_video_fullscreen_enter");
  };

  const showBigPlayButton = isBlocked || (hasEverPlayed && !isPlaying);
  const showControlsCluster = hasEverPlayed;

  return (
    <div className="pwp-hero-video-wrap" ref={containerRef}>
      <div className="pwp-hero-video-badge">
        <span className="pwp-hero-video-badge-icon">🎬</span>
        <span>Real Student. Real Results.</span>
      </div>

      <div className="pwp-hero-video-frame" ref={frameRef}>
        <video
          ref={videoRef}
          className="pwp-hero-video"
          src={HERO_VIDEO_SRC}
          poster={HERO_VIDEO_POSTER}
          muted
          autoPlay
          playsInline
          loop
          preload="auto"
          onClick={handleVideoTap}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="button"
          aria-label="Play or pause the Piano With Pan video"
        >
          Your browser doesn&apos;t support embedded video.{" "}
          <a href={HERO_VIDEO_SRC}>Watch the Piano With Pan video here.</a>
        </video>

        {showUnmutePill && (
          <button type="button" className="pwp-hero-video-unmute" onClick={handleMuteToggle}>
            🔈 Tap for sound
          </button>
        )}

        {showBigPlayButton && (
          <button
            type="button"
            className="pwp-hero-video-playbtn"
            onClick={handleManualPlayClick}
            aria-label="Play video"
          >
            <span className="pwp-hero-video-playbtn-icon">▶</span>
          </button>
        )}

        {showControlsCluster && (
          <>
            <div className="pwp-hero-video-controls">
              <button
                type="button"
                className="pwp-hero-video-ctrl-btn"
                onClick={handleMuteToggle}
                aria-label={isMuted ? "Unmute video" : "Mute video"}
              >
                {isMuted ? "🔇" : "🔊"}
              </button>

              <button
                type="button"
                className="pwp-hero-video-ctrl-btn"
                onClick={handleFullscreenClick}
                aria-label="Toggle fullscreen"
              >
                ⛶
              </button>
            </div>

            <div
              className="pwp-hero-video-progress-track"
              ref={progressTrackRef}
              onClick={handleProgressClick}
              onKeyDown={handleProgressKeyDown}
              tabIndex={0}
              role="slider"
              aria-label="Video progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progressPct)}
            >
              <div
                className="pwp-hero-video-progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DORON SLEEP PUZZLE (pre-game teaser modal)
───────────────────────────────────────────── */
function DoronSleepPuzzle({ onComplete }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [wrongFlash, setWrongFlash] = useState(false);
  const [doronSleeping, setDoronSleeping] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [gameStartTime, setGameStartTime] = useState(null);
  const wrongTimeoutRef = useRef(null);

  const viewportWidth = useViewportWidth();
  const pianoSize = getResponsivePianoSize(viewportWidth);

  const normalizeNote = useCallback((note) => {
    if (!note) return "";
    return vpNormalizeNoteName
      ? vpNormalizeNoteName(String(note).trim().toUpperCase())
      : String(note).trim().toUpperCase();
  }, []);

  useEffect(() => {
    return () => { if (wrongTimeoutRef.current) clearTimeout(wrongTimeoutRef.current); };
  }, []);

  const handleNoteClick = useCallback(
    (noteName) => {
      if (completed) return;

      const played = normalizeNote(noteName);
      const expected = normalizeNote(MELODY[currentIdx]?.note);

      if (!hasInteracted) {
        setHasInteracted(true);
        setGameStartTime(Date.now());
        trackEvent("pregame_puzzle_started", { first_note_played: played });
      }

      if (played === expected) {
        const nextIdx = currentIdx + 1;
        setCurrentIdx(nextIdx);
        setWrongFlash(false);
        if (wrongTimeoutRef.current) clearTimeout(wrongTimeoutRef.current);

        trackEvent("pregame_puzzle_correct_note", {
          note: played, note_index: currentIdx, progress: `${nextIdx}/${MELODY.length}`,
        });

        if (nextIdx >= MELODY.length) {
          const t = gameStartTime ? Date.now() - gameStartTime : 0;
          trackEvent("pregame_puzzle_completed", {
            completion_time_seconds: Math.round(t / 1000), total_notes: MELODY.length,
          });
          setTimeout(() => setDoronSleeping(true), 300);
          setTimeout(() => { setCompleted(true); onComplete?.(); }, 800);
        }
      } else {
        setWrongFlash(true);
        trackEvent("pregame_puzzle_wrong_note", { expected, played, note_index: currentIdx });
        if (wrongTimeoutRef.current) clearTimeout(wrongTimeoutRef.current);
        wrongTimeoutRef.current = setTimeout(() => setWrongFlash(false), 350);
      }
    },
    [currentIdx, completed, normalizeNote, hasInteracted, gameStartTime, onComplete]
  );

  const handleReset = useCallback(() => {
    trackEvent("pregame_puzzle_reset");
    setCurrentIdx(0);
    setCompleted(false);
    setDoronSleeping(false);
    setWrongFlash(false);
    setHasInteracted(false);
    setGameStartTime(null);
  }, []);

  const pct = (Math.min(currentIdx, MELODY.length) / MELODY.length) * 100;
  const currentNote = completed ? null : MELODY[currentIdx]?.note;

  return (
    <div className="pwp-piano-card">
      <div className="pwp-piano-hero">
        <img
          src={doronSleeping ? SLEEPING_DORON_IMG : DORON_IMG}
          alt={doronSleeping ? "Sleeping Doron" : "Doron the Dragon"}
          className="pwp-piano-hero-img"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>

      <div className="pwp-piano-dots">
        {MELODY.map((m, idx) => {
          const isPlayed = idx < currentIdx;
          const isCurrent = idx === currentIdx && !completed;
          const isWrong = isCurrent && wrongFlash;

          let cls = "pwp-piano-dot";
          if (isPlayed || completed) cls += " pwp-piano-dot-done";
          else if (isWrong) cls += " pwp-piano-dot-wrong";
          else if (isCurrent) cls += " pwp-piano-dot-current";

          return <span key={idx} className={cls}>{m.note}</span>;
        })}
      </div>

      <div className="pwp-piano-progress">
        <div
          className="pwp-piano-progress-fill"
          style={{ width: `${pct}%`, background: completed ? "#22c55e" : "#4F46E5" }}
        />
      </div>

      {!completed ? (
        <div className="pwp-piano-instruction">
          <span className="pwp-piano-point">👇</span>
          <span>Tap <span className="pwp-piano-target">{currentNote}</span> on the piano</span>
        </div>
      ) : (
        <div className="pwp-piano-instruction pwp-piano-instruction-done">
          <span>🎉</span>
          <span>Great! Doron is asleep.</span>
        </div>
      )}

      <div
        className={`pwp-piano-board ${completed ? "pwp-piano-board-done" : ""}`}
        data-current-note={currentNote || ""}
      >
        <div className="pwp-piano-wrapper">
          <VirtualPiano
            onNoteClick={handleNoteClick}
            keyWidth={pianoSize.keyWidth}
            whiteKeyHeight={pianoSize.whiteKeyHeight}
            blackKeyWidth={pianoSize.blackKeyWidth}
            blackKeyHeight={pianoSize.blackKeyHeight}
            firstKey="C4"
            lastKey="C5"
            showAllBlackLabels={false}
            blackKeyLabels={{}}
          />
        </div>
      </div>

      {completed && (
        <button onClick={handleReset} className="pwp-piano-replay">↻ Play again</button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   OUTCOMES
───────────────────────────────────────────── */
function OutcomesSection() {
  return (
    <section className="pwp-outcomes-section">
      <div className="pwp-outcomes-inner">
        <div className="pwp-outcomes-eyebrow">
          <span className="pwp-outcomes-eyebrow-pill">🎯 What your child will learn</span>
        </div>

        <h2 className="pwp-outcomes-title">
          From complete beginner to{" "}
          <span className="pwp-outcomes-title-accent">playing real songs</span>
        </h2>

        <div className="pwp-outcomes-grid">
          {OUTCOMES.map((item) => (
            <div
              key={item.title}
              className="pwp-outcome-card"
              style={{ background: item.bg, color: item.color }}
            >
              <div className="pwp-outcome-icon">{item.emoji}</div>
              <div className="pwp-outcome-title-text">{item.title}</div>
              <div className="pwp-outcome-desc" style={{ color: item.subColor }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   PLANS
───────────────────────────────────────────── */
function PlansSection({ onStart }) {
  return (
    <section className="pwp-plans-section">
      <div className="pwp-plans-inner">
        <h2 className="pwp-plans-title">
          Try it free,{" "}
          <span className="pwp-plans-title-accent">upgrade when ready</span>
        </h2>

        <p className="pwp-plans-sub">No credit card required to start.</p>

        <div className="pwp-plans-coffee">
          <span>☕</span>
          <span>
            Learn &amp; practice 10 piano songs for the cost of{" "}
            <strong>one cup of coffee</strong>
          </span>
        </div>

        <div className="pwp-plans-grid">
          <div className="pwp-plan-card pwp-plan-free">
            <div className="pwp-plan-badge pwp-plan-badge-free">Free Plan</div>
            <ul className="pwp-plan-list">
              {[
                "Level 1: Twinkle Twinkle",
                "Level 2: Happy Birthday",
                "Level 3: Ode to Joy",
                "Levels 1–3: completely free, no time limit",
                "Learn Your Notes — unlimited",
                "Practice Mode — unlimited",
                "Progress saved across devices",
              ].map((item) => (
                <li key={item} className="pwp-plan-item">
                  <span className="pwp-plan-check">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pwp-plan-card pwp-plan-premium">
            <div className="pwp-plan-badge pwp-plan-badge-premium">Full Access Monthly</div>
            <div className="pwp-plan-price">
              <span className="pwp-plan-currency">₹</span>
              <span className="pwp-plan-amount">299</span>
              <span className="pwp-plan-period">/ month</span>
            </div>
            <ul className="pwp-plan-list">
              {[
                "Everything in Free Plan",
                "Unlimited practice time for 30 days",
                "All 10 songs unlocked",
                "Final battle with Doron + all trophies",
                "30-day full access — no auto renew",
              ].map((item) => (
                <li key={item} className="pwp-plan-item">
                  <span className="pwp-plan-check">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <button
          type="button"
          onClick={onStart}
          className="pwp-cta pwp-plans-cta"
          data-analytics="get_started_plans"
        >
          Get Started <span className="pwp-cta-arrow">→</span>
        </button>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   QUOTES  — bigger, more atmospheric layout
───────────────────────────────────────────── */
function QuotesSection() {
  return (
    <section className="pwp-quotes-section">
      <div className="pwp-quotes-inner">
        <div className="pwp-section-eyebrow">Real parents. Real homes. Real results.</div>

        <h2 className="pwp-quotes-title">
          Practice that{" "}
          <span className="pwp-quotes-title-accent">actually happens</span>
        </h2>

        <p className="pwp-quotes-sub">
          No battles. No boredom. Just kids who remind <em>you</em> it&apos;s time to play.
        </p>

        <div className="pwp-quotes-grid">
          <div className="pwp-quote pwp-quote-large">
            <div className="pwp-quote-stars">★★★★★</div>
            <p className="pwp-quote-text">
              My 6-year-old played Happy Birthday for her grandmother after just 2 weeks.
              She didn&apos;t want to stop. We had to set a bedtime rule for piano practice.
            </p>
            <div className="pwp-quote-author-row">
              <div className="pwp-quote-avatar">P</div>
              <div>
                <div className="pwp-quote-author-name">Priya</div>
                <div className="pwp-quote-author-detail">Parent of Aanya, age 6 · Mumbai</div>
              </div>
            </div>
          </div>

          <div className="pwp-quote pwp-quote-large">
            <div className="pwp-quote-stars">★★★★★</div>
            <p className="pwp-quote-text">
              Finally, screen time that actually teaches something real.
              He reminds ME about practice now — I never thought that would happen.
            </p>
            <div className="pwp-quote-author-row">
              <div className="pwp-quote-avatar">R</div>
              <div>
                <div className="pwp-quote-author-name">Rahul</div>
                <div className="pwp-quote-author-detail">Parent of 8-year-old · Delhi</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   DORON INLINE SECTION
   The "Put Doron to sleep" mini-game lives here as
   a fun, invitation-style section at the bottom of
   the landing page — no modal, always visible.
───────────────────────────────────────────── */
function DoronSection({ onStart }) {
  const [puzzleComplete, setPuzzleComplete] = useState(false);

  const handleComplete = useCallback(() => {
    setPuzzleComplete(true);
    trackEvent("inline_puzzle_completed");
    setTimeout(() => onStart(), 1000);
  }, [onStart]);

  return (
    <section className="pwp-doron-section">
      <div className="pwp-doron-inner">
        <div className="pwp-section-eyebrow">Try it right now — no sign up needed</div>

        <h2 className="pwp-doron-title">
          Can you put{" "}
          <span className="pwp-doron-title-accent">Doron to sleep?</span> 🐉💤
        </h2>

        <p className="pwp-doron-sub">
          Play the 7 notes below in order. This is exactly how the game works inside.
        </p>

        <div className="pwp-doron-card-wrap">
          <DoronSleepPuzzle onComplete={handleComplete} />
        </div>

        {!puzzleComplete && (
          <button
            type="button"
            onClick={onStart}
            className="pwp-doron-skip"
          >
            Skip the preview — Start free →
          </button>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   MAIN
───────────────────────────────────────────── */
function FaqSection() {
  const [openIdx, setOpenIdx] = useState(null);

  const handleToggle = (idx, q) => {
    const willOpen = openIdx !== idx;
    setOpenIdx(willOpen ? idx : null);
    trackEvent("faq_toggle", { question: q, action: willOpen ? "open" : "close" });
  };

  return (
    <section className="pwp-section">
      <div className="pwp-section-inner">
        <div className="pwp-section-eyebrow">Quick questions</div>
        <h2 className="pwp-section-title">Parents ask, we answer</h2>
        <div className="pwp-faqs">
          {FAQS.map((faq, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div key={idx} className={`pwp-faq ${isOpen ? "pwp-faq-open" : ""}`}>
                <button
                  type="button"
                  onClick={() => handleToggle(idx, faq.q)}
                  className="pwp-faq-q"
                >
                  <span>{faq.q}</span>
                  <span className={`pwp-faq-icon ${isOpen ? "pwp-faq-icon-open" : ""}`}>+</span>
                </button>
                {isOpen && <div className="pwp-faq-a">{faq.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   MAIN
───────────────────────────────────────────── */
export default function NewHomeTwo() {
  const nav = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandaloneApp, setIsStandaloneApp] = useState(false);
  const trackedScrollRef = useRef(false);

  useEffect(() => { trackPageView("/landing"); }, []);

  useEffect(() => {
    setIsStandaloneApp(detectPlatform().isStandalone);

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      trackEvent("pwa_install_available");
    };
    const onAppInstalled = () => {
      trackEvent("pwa_installed");
      setDeferredPrompt(null);
      setIsStandaloneApp(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (trackedScrollRef.current) return;
      if (window.scrollY > 200) {
        trackedScrollRef.current = true;
        trackEvent("scroll_depth", { depth_percentage: 50 });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleStart = useCallback((location) => {
    trackEvent("cta_click", { location, cta_text: "Start Free" });
    pixelLead(location); // ← Meta Pixel: fires "Lead" for ad optimization
    nav("/game-menu");
  }, [nav]);

  const handleDownloadClick = useCallback(() => {
    trackEvent("download_app_click", { location: "navbar" });
    setShowDownloadModal(true);
  }, []);

  const handleDownloadModalClose = useCallback(() => setShowDownloadModal(false), []);
  const handleNativeInstallUsed = useCallback(() => setDeferredPrompt(null), []);

  const handleAuthSuccess = useCallback(() => {
    setShowAuthModal(false);
    nav("/game-menu");
  }, [nav]);

  return (
    <div className="pwp-home-root">
      <NavBar
        nav={nav}
        onDownloadClick={handleDownloadClick}
        showDownload={!isStandaloneApp}
      />

      <main className="pwp-home-main">
        <section className="pwp-hero">
          <div className="pwp-hero-text">
            <h1 className="pwp-hero-title">
              Piano lessons <span className="pwp-hero-title-accent">kids love</span>
            </h1>
            <p className="pwp-hero-sub">10 songs. 1 Dragon. Unlimited Fun.</p>
            <button
              type="button"
              onClick={() => handleStart("hero")}
              className="pwp-cta"
              data-analytics="get_started_hero"
            >
              Start free <span className="pwp-cta-arrow">→</span>
            </button>
          </div>

          <div className="pwp-hero-piano">
            <LandingHeroVideo />
          </div>
        </section>

        <OutcomesSection />
        <QuotesSection />
        <PlansSection onStart={() => handleStart("plans")} />
        <FaqSection />
        <DoronSection onStart={() => handleStart("doron")} />

        <section className="pwp-final-cta-section">
          <button
            type="button"
            onClick={() => handleStart("final")}
            className="pwp-cta"
            data-analytics="get_started_final"
          >
            Start free <span className="pwp-cta-arrow">→</span>
          </button>
          <p className="pwp-final-sub">Levels 1–3 free. No card needed.</p>
        </section>
      </main>

      <footer className="pwp-footer">
        <div className="pwp-footer-row">
          <button
            onClick={() => { trackEvent("footer_link_click", { destination: "privacy" }); nav("/privacy-policy"); }}
            className="pwp-footer-link"
          >
            Privacy
          </button>
          <span className="pwp-footer-dot">·</span>
          <button
            onClick={() => { trackEvent("footer_link_click", { destination: "terms" }); nav("/terms"); }}
            className="pwp-footer-link"
          >
            Terms
          </button>
        </div>
        <div className="pwp-footer-copy">© {new Date().getFullYear()} Piano With Pan · Mumbai</div>
      </footer>

      {showDownloadModal && (
        <DownloadAppModal
          onClose={handleDownloadModalClose}
          deferredPrompt={deferredPrompt}
          onNativeInstallUsed={handleNativeInstallUsed}
        />
      )}

      {showAuthModal && (
        <AuthModal
          onSuccess={handleAuthSuccess}
          onClose={() => setShowAuthModal(false)}
          message="Sign in to save your progress"
          showBenefits={true}
          showContinueWithoutLogin={true}
          onContinueWithoutLogin={() => { setShowAuthModal(false); nav("/game-menu"); }}
        />
      )}
    </div>
  );
}